import { useRef } from 'react';

import { cn } from '@/utils/cn';

import { STATUS_TONES } from './status';

/** The dot beside a status tab, in the tone that status has everywhere else. */
const DOTS = {
  neutral: 'bg-ink-muted/50',
  info: 'bg-info-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
};

/**
 * The status switcher at the top of a list ("Pending · Verified · Rejected").
 *
 * Six pages used to hand-roll this row as a copy-pasted string of classes,
 * none of them reachable by arrow key. This is the one version: underlined
 * tabs, as in the reference dashboard, that scroll sideways on a phone instead
 * of wrapping onto a second line, with Left/Right/Home/End moving between tabs as the ARIA tabs
 * pattern expects.
 *
 * `tabs` is `[{ key, label, count?, tone? }]`; `onChange(key)` fires on
 * selection. A tab whose key is a known status (PENDING, VERIFIED, REJECTED…)
 * gets a dot in that status's tone from `status.js`, so the tabs read in the
 * same colours as the badges in the list below; `tone` sets one explicitly,
 * and anything else -- "All", a role, a view -- gets no dot rather than a
 * guessed colour.
 * The shell's `Tabs` (with icons) switches whole sections of a page; this
 * filters the list beneath it. Both draw the same underline.
 */
export function SegmentedTabs({
  tabs,
  value,
  onChange,
  label = 'Filter',
  variant = 'boxed',
  className,
}) {
  // `plain` drops the frame and the margin: inside a list card's toolbar the
  // card is already the frame.
  const plain = variant === 'plain';
  const refs = useRef([]);

  function onKeyDown(event, index) {
    const last = tabs.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    refs.current[next]?.focus();
    onChange(tabs[next].key);
  }

  return (
    <div
      className={cn(
        'tab-strip max-w-full',
        // Underlined tabs, as in the reference. Standing alone they draw their
        // own hairline; in a list card's toolbar, the toolbar's rule is enough.
        !plain && 'tab-strip-rule mb-4',
        className,
      )}
    >
      <div role="tablist" aria-label={label} className="flex items-center gap-5">
        {tabs.map((tab, index) => {
          const isActive = tab.key === value;
          const tone = tab.tone ?? STATUS_TONES[tab.key];
          return (
            <button
              key={tab.key || 'all'}
              ref={(element) => {
                refs.current[index] = element;
              }}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(tab.key)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 border-b-2 px-0.5 py-2 text-[13px] font-medium whitespace-nowrap transition duration-150',
                'focus-visible:outline-brand-600 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:-outline-offset-2',
                isActive
                  ? 'border-brand-600 text-brand-600'
                  : 'text-ink-muted hover:text-ink hover:border-line border-transparent',
              )}
            >
              {tone && (
                <span
                  aria-hidden="true"
                  className={cn('size-1.5 shrink-0 rounded-full', DOTS[tone] ?? DOTS.neutral)}
                />
              )}
              {tab.label}
              {tab.count != null && (
                <span
                  className={cn(
                    'min-w-4 rounded-full px-1.5 text-center text-[10px] leading-4 font-semibold tabular-nums',
                    isActive ? 'bg-brand-50 text-brand-700' : 'bg-surface-muted text-ink-muted',
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
